"use client";
import React, { useEffect, useRef } from "react";
import Image from "next/image";
import styles from "./DmsLifecycle.module.css";
import { dmsLifecycleSteps as STEPS } from "../../data/document-management-system";
import { useServiceData } from "../../hooks/useServiceData";

export default function DmsLifecycleSection() {
  const { data: backendPageData } = useServiceData();
  const timelineRef = useRef(null);

  // Find dynamic process section
  const dynamicSection = backendPageData?.extraSections?.find(
    (s) => s.type === "processSection"
  )?.refId;

  // Fallback values
  const displayTag = dynamicSection?.tag || "How Our DMS Works — The Intelligent Document Lifecycle";
  const displayTitle = dynamicSection?.title || "From Upload to Insight — Every Step Automated.";
  const displaySubtitle = dynamicSection?.subtitle || "CodeQlik’s Document Management System transforms the way your organization handles information. It intelligently captures, organizes, secures, and distributes documents across departments — creating a single, dynamic source of truth.";
  
  const displaySteps = dynamicSection?.steps?.map((s, idx) => ({
    no: String(idx + 1).padStart(2, '0'),
    title: s.title,
    desc: s.description,
    icon: s.icon
  })) || STEPS;

  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("http")) return url;
    return url;
  };

  useEffect(() => {
    const timelineEl = timelineRef.current;
    if (!timelineEl) return;

    const spineObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.inView);
          }
        });
      },
      { threshold: 0.2 }
    );
    spineObserver.observe(timelineEl);

    const itemSelector = `.${styles.item}`;
    const items = timelineEl.querySelectorAll(itemSelector);
    const itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.inView);
            itemObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25, rootMargin: "0px 0px -10% 0px" }
    );
    items.forEach((el) => itemObserver.observe(el));

    return () => {
      spineObserver.disconnect();
      itemObserver.disconnect();
    };
  }, []);

  return (
    <section className={`${styles.section} py-5`}>
      <div className="container">
        <header className={styles.head} data-reveal="fade">
          <div className={styles.eyebrow}>{displayTag}</div>
          <h3 className={styles.title}>{displayTitle}</h3>
          <p className={styles.subtitle}>{displaySubtitle}</p>
        </header>

        <div ref={timelineRef} className={styles.timeline} data-reveal="stagger-mix">
          {displaySteps.map((s, idx) => {
            const sideClass = idx % 2 === 0 ? styles.left : styles.right;
            return (
              <div key={s.no || idx} className={`${styles.item} ${sideClass}`} data-reveal="slide-up">
                <span className={styles.node} aria-hidden></span>
                <div className={styles.content}>
                  <span className={styles.no}>{s.no}</span>
                  <span className={styles.icon} aria-hidden style={{ position: 'relative' }}>
                    {(() => {
                      const iconUrl = typeof s.icon === 'string' ? s.icon : (s.icon?.url || '');
                      const isImage = typeof iconUrl === 'string' && (iconUrl.startsWith('http') || iconUrl.includes('/') || iconUrl.includes('.'));
                      return isImage ? (
                        <Image src={getImageUrl(iconUrl)} alt={s.title} style={{ objectFit: 'contain' }} fill />
                      ) : (
                        <i className={iconUrl}></i>
                      );
                    })()}
                  </span>
                  <h4 className={styles.cardTitle}>{s.title}</h4>
                  <p className={styles.cardDesc}>{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <p className={styles.tagline} data-reveal="fade">
          <em>Every document. Every version. Every detail — intelligently managed.</em>
        </p>
      </div>
    </section>
  );
}
